<template>
  <el-dialog
    title="管理随堂内容"
    :visible.sync="dialogVisible"
    width="95%"
    top="4vh"
    @close="handleClose"
  >
    <div class="question-manager" v-if="dialogVisible">
      <!-- 左侧：视频播放区域 (50% 宽度) -->
      <div class="manager-left" v-if="videoUrl">
        <div class="preview-video-container">
          <video
            ref="previewVideo"
            :src="videoUrl"
            controls
            preload="metadata"
            playsinline
            style="width: 100%; max-height: 520px; border-radius: 4px;"
            @loadedmetadata="onVideoLoaded"
            @timeupdate="onVideoTimeUpdate"
          ></video>

          <!-- 自定义进度条标签 -->
          <div v-if="videoMarkers.length > 0 && videoDuration > 0" class="video-markers">
            <!-- 标签进度轨道 (点击可跳转) -->
            <div class="markers-timeline-track" @click="onMarkersTimelineClick">
              <div class="markers-timeline-played" :style="{ width: (lastVideoTime / videoDuration) * 100 + '%' }"></div>
              <div class="markers-timeline-handle" :style="{ left: (lastVideoTime / videoDuration) * 100 + '%' }"></div>
            </div>

            <div
              v-for="marker in videoMarkers"
              :key="marker.id || marker.sort"
              class="video-marker"
              :style="{left: getMarkerPosition(marker.timeSeconds) + '%'}"
              @click="jumpToTime(marker.timeSeconds)"
            >
              <div class="marker-label">{{ marker.label || '标签' }}</div>
              <div class="marker-line"></div>
              <div class="marker-dot"></div>
            </div>
          </div>
        </div>

        <div style="color: #606266; font-size: 14px; margin-top: 15px; font-weight: 500;">
          <i class="el-icon-video-play"></i> 视频总时长：{{ formatDuration(videoDuration) }}
        </div>
      </div>

      <!-- 右侧：管理操作区域 (50% 宽度) -->
      <div class="manager-right">
        <!-- 说明 -->
        <el-alert
          title="在视频中插入题目或交互式投票插件，学生播放到指定时间时视频会暂停，待学生操作或倒计时结束后自动继续播放"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 15px;"
        >
        </el-alert>

        <!-- 题目/内容列表 -->
        <div class="questions-list">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-weight: 600; font-size: 14px; color: #303133;">
              <i class="el-icon-collection-tag"></i> 视频播放内容列表
            </span>
            <div style="display: flex; gap: 5px;">
              <el-button type="primary" size="mini" icon="el-icon-document-add" @click="openSelectQuestion">
                添加随测题
              </el-button>
              <el-button type="warning" size="mini" icon="el-icon-pie-chart" @click="openAddVoteWidget">
                添加投票器
              </el-button>
            </div>
          </div>

          <div class="table-scroll-wrapper">
            <el-table :data="videoQuestions" border style="width: 100%" size="mini">
              <el-table-column label="#" width="40" align="center">
                <template slot-scope="scope">
                  {{ scope.$index + 1 }}
                </template>
              </el-table-column>

              <el-table-column label="弹出时间" width="165">
                <template slot-scope="scope">
                  <div style="display: flex; gap: 4px; align-items: center;">
                    <el-time-picker
                      v-model="scope.row.timeValue"
                      format="HH:mm:ss"
                      value-format="HH:mm:ss"
                      placeholder="时间"
                      size="mini"
                      style="width: 100px;"
                      @change="onTimeChange(scope.row)"
                      :picker-options="{
                        selectableRange: getSelectableRange()
                      }"
                    >
                    </el-time-picker>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-video-camera"
                      @click="setCurrentTime(scope.row)"
                      title="使用当前时间"
                      style="padding: 4px;"
                      circle
                      plain
                    ></el-button>
                  </div>
                  <div style="font-size: 10px; color: #909399; margin-top: 2px;">
                    {{ scope.row.popTimeSeconds }} 秒
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="内容类型 / 详情" min-width="120">
                <template slot-scope="scope">
                  <!-- 如果是随堂投票器插件 -->
                  <div v-if="scope.row.questionText && scope.row.questionText.startsWith('[VOTE_')" class="widget-preview-card">
                    <div style="font-weight: 600; color: #E6A23C; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">
                      <i class="el-icon-pie-chart"></i>
                      随堂投票插件 ({{ scope.row.questionText.startsWith('[VOTE_SINGLE]') ? '单选' : '多选' }})
                    </div>
                    <div class="widget-title" style="font-weight: 500; font-size: 12px; color: #303133; margin-bottom: 3px;">
                      {{ cleanVoteTitle(scope.row.questionText) }}
                    </div>
                    <div style="font-size: 10px; color: #909399; display: flex; gap: 4px; flex-wrap: wrap;">
                      <span v-for="(opt, idx) in parseOptions(scope.row.optionset)" :key="idx" style="background: #FFF7E6; border: 1px solid #FFE7C6; padding: 1px 4px; border-radius: 2px;">
                        {{ opt.key }}.{{ opt.value }}
                      </span>
                    </div>
                  </div>
                  <!-- 普通随堂测试题目 -->
                  <div v-else class="question-preview">
                    <div style="font-weight: 600; color: #409EFF; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">
                      <i class="el-icon-document"></i>
                      随堂随测题
                    </div>
                    <div class="question-text" style="font-size: 12px; color: #303133;" v-html="renderLatex(scope.row.questionText)"></div>
                    <div class="question-meta" style="margin-top: 2px;">
                      <el-tag size="mini" type="success">答案：{{ scope.row.answer }}</el-tag>
                    </div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="限时(秒)" width="85" align="center">
                <template slot-scope="scope">
                  <el-input-number
                    v-model="scope.row.durationSeconds"
                    :min="10"
                    :max="300"
                    :step="10"
                    size="mini"
                    controls-position="right"
                    style="width: 70px;"
                  ></el-input-number>
                </template>
              </el-table-column>

              <el-table-column label="必操作" width="50" align="center">
                <template slot-scope="scope">
                  <el-switch v-model="scope.row.isRequired" size="mini"></el-switch>
                </template>
              </el-table-column>

              <el-table-column label="操作" width="45" align="center">
                <template slot-scope="scope">
                  <el-button
                    type="danger"
                    size="mini"
                    icon="el-icon-delete"
                    @click="deleteQuestion(scope.$index)"
                    style="padding: 4px;"
                    circle
                  ></el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div v-if="videoQuestions.length === 0" style="text-align: center; padding: 20px; color: #909399; font-size: 12px;">
            <i class="el-icon-folder-opened" style="font-size: 32px; margin-bottom: 5px; color: #dcdfe6;"></i>
            <p style="margin: 0;">暂无随堂题目，点击右上方“添加”开始</p>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false" size="medium">
        <i class="el-icon-close"></i> 取消
      </el-button>
      <el-button
        type="primary"
        @click="saveQuestions"
        :loading="saving"
        size="medium"
      >
        <i class="el-icon-check"></i> {{ saving ? '保存中...' : '保存设置' }}
      </el-button>
    </div>

    <!-- 选择题目对话框 -->
    <el-dialog
      title="选择题目"
      :visible.sync="selectQuestionVisible"
      width="1000px"
      append-to-body
    >
      <div class="question-selector-toolbar">
        <el-radio-group v-model="questionSource" size="small" @change="handleQuestionSourceChange">
          <el-radio-button label="course">我的题库</el-radio-button>
          <el-radio-button label="all">总题库</el-radio-button>
        </el-radio-group>
        <el-input
          v-model="searchKeyword"
          placeholder="搜索题目内容"
          size="small"
          class="question-selector-search"
          clearable
          @clear="handleQuestionFilterChange"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleQuestionFilterChange"></el-button>
        </el-input>
        <el-button size="small" icon="el-icon-refresh" :disabled="loadingQuestions" @click="handleQuestionFilterChange">刷新</el-button>
      </div>

      <!-- 题目列表 -->
      <el-table
        ref="questionTable"
        :data="availableQuestions"
        v-loading="loadingQuestions"
        border
        style="width: 100%"
        max-height="450"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="50" align="center"></el-table-column>
        <el-table-column type="index" width="60" label="#" align="center"></el-table-column>
        <el-table-column label="题目内容" min-width="400">
          <template slot-scope="scope">
            <div class="question-preview">
              <div class="question-text" v-html="renderLatex(scope.row.contentCn)"></div>
              <div v-if="scope.row.contentEn" class="question-text-en" v-html="renderLatex(scope.row.contentEn)"></div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="答案" width="80" align="center">
          <template slot-scope="scope">
            <el-tag size="small" type="success">{{ scope.row.answer }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="difficulty" label="难度" width="80" align="center"></el-table-column>
        <el-table-column label="知识点" width="180" show-overflow-tooltip>
          <template slot="header">
            <div class="question-knowledge-header">
              <span>知识点</span>
              <el-popover
                placement="bottom-end"
                trigger="click"
                width="260"
                @show="handleKnowledgePopoverShow"
              >
                <el-cascader
                  :key="knowledgeCascaderKey"
                  v-model="knowledgeFilter"
                  placeholder="按知识点筛选"
                  clearable
                  filterable
                  size="small"
                  :options="knowledgeOptions"
                  :props="knowledgeCascaderProps"
                  style="width: 100%;"
                  @change="handleQuestionFilterChange"
                />
                <el-button slot="reference" type="text" size="mini">筛选</el-button>
              </el-popover>
            </div>
          </template>
          <template slot-scope="scope">
            {{ getQuestionKnowledgeText(scope.row) }}
          </template>
        </el-table-column>
      </el-table>

      <div style="margin-top: 15px; text-align: right;" v-if="questionSource === 'all' && totalQuestions > pageSize">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="pageSize"
          :total="totalQuestions"
          layout="total, prev, pager, next"
          small
        />
      </div>

      <div slot="footer">
        <el-button @click="selectQuestionVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSelectQuestions" :disabled="selectedQuestions.length === 0">
          确定添加 ({{ selectedQuestions.length }})
        </el-button>
      </div>
    </el-dialog>

    <!-- 随堂投票器配置对话框 -->
    <el-dialog
      title="配置随堂投票器插件"
      :visible.sync="voteDialogVisible"
      width="550px"
      append-to-body
    >
      <el-form :model="voteForm" label-width="100px" size="small" ref="voteForm" :rules="voteRules">
        <!-- 模板快捷载入 -->
        <el-form-item label="投票模板" v-if="voteTemplates.length > 0">
          <el-select v-model="selectedTemplate" placeholder="可选择已有模板快速填入" style="width: 100%;" @change="loadVoteTemplate" clearable>
            <el-option
              v-for="(tpl, idx) in voteTemplates"
              :key="idx"
              :label="tpl.title + ' (' + (tpl.type === 'SINGLE' ? '单选' : '多选') + ')'"
              :value="idx"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="投票标题" prop="title">
          <el-input v-model="voteForm.title" placeholder="请输入投票标题，如：本知识点大家掌握情况如何？" maxlength="100" show-word-limit></el-input>
        </el-form-item>

        <!-- 弹出时间（时间轴设置） -->
        <el-form-item label="弹出时间" prop="timeValue" v-if="voteForm.timeValue !== undefined">
          <div style="display: flex; gap: 8px; align-items: center;">
            <el-time-picker
              v-model="voteForm.timeValue"
              format="HH:mm:ss"
              value-format="HH:mm:ss"
              placeholder="请选择弹出时间"
              style="width: 130px;"
              @change="voteForm.popTimeSeconds = timeStringToSeconds(voteForm.timeValue)"
              :picker-options="{
                selectableRange: getSelectableRange()
              }"
            ></el-time-picker>
            <el-button
              type="primary"
              icon="el-icon-video-camera"
              size="mini"
              @click="setVotePopTimeToCurrent"
              plain
            >同步视频当前时间轴 ({{ voteForm.popTimeSeconds }}秒)</el-button>
          </div>
          <div style="margin-top: 4px; color: #909399; font-size: 11px; line-height: 1.4;">
            控制投票插件在视频播放到指定时间点自动暂停弹出。
          </div>
        </el-form-item>

        <el-form-item label="选择类型" prop="type">
          <el-radio-group v-model="voteForm.type">
            <el-radio label="SINGLE">单选投票</el-radio>
            <el-radio label="MULTI">多选投票</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="投票选项">
          <div v-for="(opt, idx) in voteForm.options" :key="idx" style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center;">
            <el-tag type="info" style="font-weight: 600;">{{ String.fromCharCode(65 + idx) }}</el-tag>
            <el-input v-model="voteForm.options[idx]" placeholder="请输入选项内容" style="flex: 1;" maxlength="50"></el-input>
            <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeVoteOption(idx)" :disabled="voteForm.options.length <= 2"></el-button>
          </div>
          <el-button type="dashed" icon="el-icon-plus" size="mini" style="width: 100%; margin-top: 5px;" @click="addVoteOption" :disabled="voteForm.options.length >= 6">添加投票选项</el-button>
        </el-form-item>

        <el-form-item label="限时时间" prop="duration">
          <el-input-number v-model="voteForm.duration" :min="10" :max="300" :step="10" style="width: 130px;"></el-input-number>
          <span style="margin-left: 8px; color: #909399; font-size: 13px;">秒 (建议 30 - 60 秒)</span>
        </el-form-item>

        <el-form-item label="保存选项">
          <el-checkbox v-model="voteForm.saveTemplate">保存为常用投票模板</el-checkbox>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="voteDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitVoteWidget" :loading="savingVoteWidget">确认添加</el-button>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getVideoQuestions, batchAddVideoQuestions, deleteVideoQuestion, getVideoMarkers } from '@/api/amcVideoEnhancement'
import { getCourseQuestions, addQuestionsToCourse } from '@/api/amcCourseQuestion'
import { quPaging, quAdd } from '@/api/amcquestion'
import { listGeneralKnowledgeTree } from '@/api/knowledge'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'VideoQuestionManager',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sectionId: {
      type: Number,
      required: false,
      default: null
    },
    courseId: {
      type: Number,
      required: false,
      default: null
    },
    repoId: {
      type: [Number, String],
      required: false,
      default: null
    },
    videoUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      videoQuestions: [],
      videoDuration: 0,
      lastVideoTime: 0,
      videoMarkers: [], // 视频时间标签
      saving: false,

      // 选择题目
      selectQuestionVisible: false,
      questionSource: 'course', // 'course' or 'all'
      availableQuestions: [],
      selectedQuestions: [],
      searchKeyword: '',
      currentPage: 1,
      pageSize: 10,
      totalQuestions: 0,
      loadingQuestions: false,
      knowledgeFilter: '',
      knowledgeOptions: [],
      knowledgeCascaderKey: 0,
      knowledgeCascaderProps: {
        value: 'id',
        label: 'content',
        children: 'children',
        leaf: 'leaf',
        checkStrictly: true,
        emitPath: false
      },
      knowledgeNameMap: {},

      // 随堂投票插件配置
      voteDialogVisible: false,
      savingVoteWidget: false,
      selectedTemplate: '',
      voteTemplates: [],
      voteForm: {
        title: '',
        type: 'SINGLE',
        options: ['', ''],
        duration: 60,
        saveTemplate: false
      },
      voteRules: {
        title: [
          { required: true, message: '请输入投票标题', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadVideoQuestions()
        this.loadVideoMarkers()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    // 加载已有视频题目
    async loadVideoQuestions() {
      if (!this.sectionId) {
        console.warn('sectionId为空，无法加载视频题目')
        this.videoQuestions = []
        return
      }

      console.log('开始加载视频题目，sectionId:', this.sectionId)
      try {
        const res = await getVideoQuestions(this.sectionId)
        console.log('加载视频题目响应:', res)

        if (res.code === 1 && res.data) {
          this.videoQuestions = res.data.map(q => ({
            ...q,
            timeValue: this.secondsToTimeString(q.popTimeSeconds),
            questionText: q.questionText || q.questionTextCn || '',
            answer: q.answer || ''
          }))
          console.log('成功加载视频题目，数量:', this.videoQuestions.length)
        } else {
          console.warn('加载视频题目失败或无数据:', res.msg)
          this.videoQuestions = []
        }
      } catch (e) {
        console.error('加载视频题目失败', e)
        this.videoQuestions = []
      }
    },

    // 打开选择题目对话框
    async openSelectQuestion() {
      this.selectQuestionVisible = true
      this.questionSource = 'all'
      this.searchKeyword = ''
      this.knowledgeFilter = ''
      this.currentPage = 1
      this.selectedQuestions = []
      await this.loadKnowledgeOptions()
      this.loadQuestions()
    },

    handleQuestionSourceChange() {
      this.currentPage = 1
      this.selectedQuestions = []
      this.availableQuestions = []
      if (this.$refs.questionTable) {
        this.$refs.questionTable.clearSelection()
      }
      this.loadQuestions()
    },

    handleQuestionFilterChange() {
      this.currentPage = 1
      this.selectedQuestions = []
      if (this.$refs.questionTable) {
        this.$refs.questionTable.clearSelection()
      }
      this.loadQuestions()
    },

    async handleKnowledgePopoverShow() {
      await this.loadKnowledgeOptions(true)
    },

    async loadKnowledgeOptions(forceReload = false) {
      try {
        if (!forceReload && this.knowledgeOptions && this.knowledgeOptions.length) {
          return
        }
        if (!this.repoId) {
          this.knowledgeOptions = []
          this.knowledgeCascaderKey += 1
          return
        }
        const res = await listGeneralKnowledgeTree(this.repoId)
        const data = res && res.data !== undefined ? res.data : res
        const list = Array.isArray(data) ? data : (data.records || data.list || [])
        this.knowledgeOptions = this.buildKnowledgeTree(list)
        this.knowledgeNameMap = {}
        this.flattenKnowledgeTree(this.knowledgeOptions)
        this.knowledgeCascaderKey += 1
      } catch (e) {
        console.error('加载知识点筛选选项失败', e)
        this.knowledgeOptions = []
        this.knowledgeNameMap = {}
        this.knowledgeCascaderKey += 1
      }
    },

    flattenKnowledgeTree(nodes) {
      ;(Array.isArray(nodes) ? nodes : []).forEach(node => {
        if (!node) return
        if (node.id !== undefined && node.id !== null) {
          this.knowledgeNameMap[String(node.id)] = node.content || node.label || node.name || String(node.id)
        }
        if (node.children && node.children.length) {
          this.flattenKnowledgeTree(node.children)
        }
      })
    },

    normalizeKnowledgeTree(nodes) {
      return (Array.isArray(nodes) ? nodes : []).map(node => {
        const item = {
          ...node,
          id: node.id,
          content: node.content || node.label || node.name || String(node.id)
        }
        if (Array.isArray(node.children) && node.children.length > 0) {
          item.children = this.normalizeKnowledgeTree(node.children)
          item.leaf = false
        } else {
          delete item.children
          item.leaf = true
        }
        return item
      })
    },

    buildKnowledgeTree(nodes) {
      const list = Array.isArray(nodes) ? nodes : []
      const hasAnyChildren = list.some(node => Array.isArray(node && node.children) && node.children.length > 0)
      if (hasAnyChildren) {
        return this.normalizeKnowledgeTree(list)
      }

      const nodeMap = {}
      const normalizedList = list.map(node => ({
        ...node,
        id: node.id,
        parentId: node.parentId,
        content: node.content || node.label || node.name || String(node.id),
        children: []
      }))

      normalizedList.forEach(node => {
        nodeMap[String(node.id)] = node
      })

      const roots = []
      normalizedList.forEach(node => {
        const parentKey = node.parentId !== undefined && node.parentId !== null ? String(node.parentId) : ''
        const parent = parentKey && parentKey !== '0' ? nodeMap[parentKey] : null
        if (parent) {
          parent.children.push(node)
        } else {
          roots.push(node)
        }
      })

      return this.normalizeKnowledgeTree(roots)
    },

    // 加载可选题目
    async loadQuestions() {
      this.loadingQuestions = true
      try {
        if (this.questionSource === 'course') {
          // 从课程题库加载
          const res = await getCourseQuestions(this.courseId)
          if (res.code === 1 && res.data) {
            let questions = res.data
            // 客户端搜索
            if (this.searchKeyword && this.searchKeyword.trim()) {
              const keyword = this.searchKeyword.trim().toLowerCase()
              questions = questions.filter(q =>
                (q.contentCn && q.contentCn.toLowerCase().includes(keyword)) ||
                (q.contentEn && q.contentEn.toLowerCase().includes(keyword))
              )
            }
            this.availableQuestions = questions
            this.totalQuestions = questions.length
          } else {
            this.availableQuestions = []
            this.totalQuestions = 0
          }
        } else {
          // 从总题库加载
          const params = {
            pageNum: this.currentPage,
            pageSize: this.pageSize,
            courseId: this.courseId
          }
          if (this.searchKeyword && this.searchKeyword.trim()) {
            params.content = this.searchKeyword.trim()
          }
          if (this.knowledgeFilter) {
            params.knowledge = this.knowledgeFilter
          }
          const res = await quPaging(params)
          if (res.code === 1 && res.data) {
            const data = res.data
            this.availableQuestions = Array.isArray(data) ? data : (data.records || data.list || [])
            this.totalQuestions = Array.isArray(data) ? this.availableQuestions.length : (data.total || this.availableQuestions.length)
          } else {
            this.availableQuestions = []
            this.totalQuestions = 0
          }
        }
      } catch (e) {
        console.error('加载题目失败', e)
        this.availableQuestions = []
      } finally {
        this.loadingQuestions = false
      }
    },

    // 处理选择变化
    handleSelectionChange(selection) {
      this.selectedQuestions = selection
    },

    handlePageChange(page) {
      this.currentPage = page
      this.loadQuestions()
    },

    getQuestionKnowledgeText(question) {
      const value = String((question && (question.knowledgeNames || question.knowledge)) || '').trim()
      if (!value) return '--'
      return value.split(',').map(item => {
        const key = item.trim()
        return this.knowledgeNameMap[key] || key
      }).join(', ')
    },

    focusKnowledgeFilter() {
      this.$message.info('请使用上方“按知识点筛选”进行筛选')
    },

    // 确认选择题目
    confirmSelectQuestions() {
      if (this.selectedQuestions.length === 0) {
        this.$message.warning('请至少选择一个题目')
        return
      }

      // 检查是否已经添加
      const existingIds = new Set(this.videoQuestions.map(q => q.questionId))
      const newQuestions = this.selectedQuestions.filter(q => !existingIds.has(q.id))

      if (newQuestions.length === 0) {
        this.$message.warning('选择的题目已全部添加')
        return
      }

      // 添加到列表
      newQuestions.forEach((q, index) => {
        const nextTime = this.videoQuestions.length > 0
          ? Math.max(...this.videoQuestions.map(vq => vq.popTimeSeconds)) + 60
          : 60

        this.videoQuestions.push({
          sectionId: this.sectionId,
          questionId: q.id,
          questionText: q.contentCn || q.contentEn || '',
          answer: q.answer || '',
          popTimeSeconds: nextTime + (index * 60),
          timeValue: this.secondsToTimeString(nextTime + (index * 60)),
          durationSeconds: 60,
          isRequired: true
        })
      })

      this.$message.success(`成功添加 ${newQuestions.length} 个题目`)
      this.selectQuestionVisible = false
      this.selectedQuestions = []
    },

    // 解析选项为 A. xxx 键值对
    parseOptions(optionset) {
      if (!optionset) {
        return []
      }
      const options = []
      let parts = []
      if (optionset.includes('|')) {
        parts = optionset.split('|')
      } else if (optionset.includes(';')) {
        parts = optionset.split(';')
      } else if (optionset.includes('\n')) {
        parts = optionset.split('\n')
      } else {
        parts = optionset.split(/(?=[A-Z][\.\)、\s])/)
      }

      parts.forEach((part) => {
        part = part.trim()
        if (!part) return

        let match = part.match(/^([A-Z])[\.\)、]\s*(.+)$/s)
        if (!match) {
          match = part.match(/^\(([A-Z])\)\s*(.+)$/s)
        }
        if (!match) {
          match = part.match(/^([A-Z])\s+(.+)$/s)
        }

        if (match) {
          options.push({
            key: match[1],
            value: match[2].trim()
          })
        } else {
          options.push({
            key: '?',
            value: part
          })
        }
      })
      return options
    },

    // 过滤掉投票器标识头，保留纯净的投票标题展示
    cleanVoteTitle(txt) {
      if (!txt) return ''
      return txt.replace(/^\[VOTE_[A-Z]+\]/, '')
    },

    // 载入本地缓存的投票模板
    loadVoteTemplatesFromLocal() {
      try {
        const local = localStorage.getItem('vote_templates')
        this.voteTemplates = local ? JSON.parse(local) : []
      } catch (e) {
        console.error('加载本地投票模板失败:', e)
        this.voteTemplates = []
      }
    },

    // 选择已有投票模板
    loadVoteTemplate(idx) {
      if (idx === '' || idx === undefined || idx === null) {
        return
      }
      const tpl = this.voteTemplates[idx]
      if (tpl) {
        this.voteForm.title = tpl.title
        this.voteForm.type = tpl.type
        this.voteForm.options = [...tpl.options]
        this.voteForm.duration = tpl.duration
      }
    },

    // 打开配置随堂投票器对话框
    openAddVoteWidget() {
      this.selectedTemplate = ''
      this.loadVoteTemplatesFromLocal()
      
      // 自动从预览播放器获取当前时间轴节点作为推荐弹出时间
      let initialPopTime = 0
      if (this.$refs.previewVideo) {
        initialPopTime = Math.floor(this.$refs.previewVideo.currentTime)
      } else if (this.videoQuestions.length > 0) {
        initialPopTime = Math.max(...this.videoQuestions.map(vq => vq.popTimeSeconds)) + 60
      } else {
        initialPopTime = 60
      }

      this.voteForm = {
        title: '',
        type: 'SINGLE',
        options: ['', ''],
        duration: 60,
        saveTemplate: false,
        popTimeSeconds: initialPopTime,
        timeValue: this.secondsToTimeString(initialPopTime)
      }
      this.voteDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.voteForm) {
          this.$refs.voteForm.clearValidate()
        }
      })
    },

    // 同步投票弹出时间为视频当前时间点
    setVotePopTimeToCurrent() {
      if (this.$refs.previewVideo) {
        const currentTime = Math.floor(this.$refs.previewVideo.currentTime)
        this.voteForm.popTimeSeconds = currentTime
        this.voteForm.timeValue = this.secondsToTimeString(currentTime)
        this.$message.success(`已成功同步时间轴: ${this.voteForm.timeValue}`)
      } else {
        this.$message.warning('请先在下方加载播放视频')
      }
    },

    // 随堂投票增加选项
    addVoteOption() {
      if (this.voteForm.options.length < 6) {
        this.voteForm.options.push('')
      }
    },

    // 随堂投票删除选项
    removeVoteOption(idx) {
      if (this.voteForm.options.length > 2) {
        this.voteForm.options.splice(idx, 1)
      }
    },

    // 确认添加投票器（在后台通过创建[VOTE_SINGLE]或[VOTE_MULTI]标题的虚拟题目实现）
    async submitVoteWidget() {
      this.$refs.voteForm.validate(async (valid) => {
        if (!valid) return

        // 验证选项
        const validOptions = this.voteForm.options.map(opt => opt.trim()).filter(Boolean)
        if (validOptions.length < 2) {
          this.$message.warning('请至少输入2个有效的投票选项')
          return
        }

        this.savingVoteWidget = true
        try {
          // 如果勾选了保存模板，保存进本地 localStorage
          if (this.voteForm.saveTemplate) {
            const templates = [...this.voteTemplates]
            // 去重
            const duplicateIdx = templates.findIndex(t => t.title === this.voteForm.title)
            const newTpl = {
              title: this.voteForm.title,
              type: this.voteForm.type,
              options: validOptions,
              duration: this.voteForm.duration
            }
            if (duplicateIdx > -1) {
              templates[duplicateIdx] = newTpl
            } else {
              templates.push(newTpl)
            }
            localStorage.setItem('vote_templates', JSON.stringify(templates))
          }

          // 创建后端虚拟题目 payload (加入独一无二的 number 字段以满足数据库约束)
          const prefix = this.voteForm.type === 'SINGLE' ? '[VOTE_SINGLE]' : '[VOTE_MULTI]'
          const optionset = validOptions.map((opt, i) => `${String.fromCharCode(65 + i)}.${opt}`).join('|')
          
          const payload = {
            number: 'VOTE_' + Date.now(),
            contentCn: prefix + this.voteForm.title,
            optionsCn: optionset,
            answer: 'VOTE', // 标记为投票性质，不要求有绝对标准答案
            difficulty: 'Low',
            analysisCn: '随堂互动投票，提交或倒计时结束继续播放。'
          }

          console.log('添加投票器插件题库记录...', payload)
          const res = await quAdd(payload)
          
          const ok = !res || typeof res.code === 'undefined' || res.code === 1 || res.code === 20000
          if (!ok) {
            this.$message.error(res.msg || '创建投票器基础数据失败')
            return
          }

          const newQuestionId = Number(res.data?.id || res.data || res.msg)
          if (!newQuestionId) {
            this.$message.error('无法解析新建投票器数据的ID')
            return
          }

          // 自动加入当前课程
          if (this.courseId) {
            try {
              await addQuestionsToCourse(this.courseId, [newQuestionId])
            } catch (e) {
              console.warn('关联题库到课程失败(不影响添加):', e)
            }
          }

          this.videoQuestions.push({
            sectionId: this.sectionId,
            questionId: newQuestionId,
            questionText: prefix + this.voteForm.title,
            optionset: optionset,
            answer: 'VOTE',
            popTimeSeconds: this.voteForm.popTimeSeconds,
            timeValue: this.voteForm.timeValue,
            durationSeconds: this.voteForm.duration,
            isRequired: true
          })

          this.$message.success('已成功在当前时间添加投票器插件！')
          this.voteDialogVisible = false
        } catch (e) {
          console.error('配置投票插件出错:', e)
          this.$message.error('添加投票插件出错: ' + (e.message || e))
        } finally {
          this.savingVoteWidget = false
        }
      })
    },

    // 删除题目
    deleteQuestion(index) {
      this.$confirm('确定删除该题目吗？', '提示', {
        type: 'warning'
      }).then(() => {
        this.videoQuestions.splice(index, 1)
        this.$message.success('删除成功')
      }).catch(() => {})
    },

    // 保存题目设置
    async saveQuestions() {
      // 验证
      for (let i = 0; i < this.videoQuestions.length; i++) {
        const q = this.videoQuestions[i]
        if (!q.questionId) {
          this.$message.warning(`第${i + 1}个题目无效`)
          return
        }
        if (q.popTimeSeconds === undefined || q.popTimeSeconds < 0) {
          this.$message.warning(`第${i + 1}个题目的弹出时间无效`)
          return
        }
        if (!q.durationSeconds || q.durationSeconds < 30) {
          this.$message.warning(`第${i + 1}个题目的答题时长不能少于30秒`)
          return
        }
      }

      const requestData = {
        sectionId: this.sectionId,
        questions: this.videoQuestions.map(q => ({
          questionId: q.questionId,
          popTimeSeconds: q.popTimeSeconds,
          durationSeconds: q.durationSeconds,
          isRequired: q.isRequired
        }))
      }

      console.log('准备保存视频题目，请求数据:', requestData)

      this.saving = true
      try {
        const res = await batchAddVideoQuestions(requestData)
        console.log('保存视频题目响应:', res)

        if (res.code === 1) {
          this.$message.success('视频问题已更新')
          this.$emit('saved')
          // 延迟关闭对话框，让用户看到成功提示
          setTimeout(() => {
            this.dialogVisible = false
          }, 500)
        } else {
          this.$message.error(res.msg || '保存失败')
        }
      } catch (e) {
        console.error('保存失败', e)
        this.$message.error('保存失败: ' + (e.message || e))
      } finally {
        this.saving = false
      }
    },

    // 将秒数转换为时间字符串 HH:mm:ss
    secondsToTimeString(seconds) {
      const hh = Math.floor(seconds / 3600)
      const mm = Math.floor((seconds % 3600) / 60)
      const ss = seconds % 60
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    },

    // 将时间字符串转换为秒数
    timeStringToSeconds(timeStr) {
      if (!timeStr) return 0
      const parts = timeStr.split(':')
      if (parts.length === 3) {
        const [hh, mm, ss] = parts.map(Number)
        return hh * 3600 + mm * 60 + ss
      }
      return 0
    },

    // 时间选择器变化
    onTimeChange(question) {
      question.popTimeSeconds = this.timeStringToSeconds(question.timeValue)
    },

    // 获取可选时间范围
    getSelectableRange() {
      if (this.videoDuration > 0) {
        const maxTime = this.secondsToTimeString(Math.floor(this.videoDuration))
        return `00:00:00 - ${maxTime}`
      }
      return '00:00:00 - 23:59:59'
    },

    // 视频加载完成
    onVideoLoaded() {
      if (this.$refs.previewVideo) {
        this.videoDuration = this.$refs.previewVideo.duration
      }
    },

    // 播放时间更新
    onVideoTimeUpdate(e) {
      this.lastVideoTime = e.target.currentTime
    },

    // 获取标签在进度条上的位置百分比
    getMarkerPosition(timeSeconds) {
      if (this.videoDuration === 0) {
        return 0
      }
      return (timeSeconds / this.videoDuration) * 100
    },

    // 跳转到指定时间
    jumpToTime(seconds) {
      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.currentTime = seconds
      }
    },

    // 点击自定义标签进度条跳转
    onMarkersTimelineClick(e) {
      if (this.videoDuration > 0 && this.$refs.previewVideo) {
        const rect = e.currentTarget.getBoundingClientRect()
        const clickX = e.clientX - rect.left
        const percentage = Math.max(0, Math.min(1, clickX / rect.width))
        const targetTime = percentage * this.videoDuration
        this.jumpToTime(targetTime)
      }
    },

    // 加载视频标签以用于进度条展示
    async loadVideoMarkers() {
      if (!this.sectionId) return
      try {
        const res = await getVideoMarkers(this.sectionId)
        if (res.code === 1 && res.data) {
          this.videoMarkers = res.data
        } else {
          this.videoMarkers = []
        }
      } catch (e) {
        console.error('加载标签进度条展示失败', e)
        this.videoMarkers = []
      }
    },

    // 设置为当前播放时间
    setCurrentTime(question) {
      if (this.$refs.previewVideo) {
        const currentTime = Math.floor(this.$refs.previewVideo.currentTime)
        question.popTimeSeconds = currentTime
        question.timeValue = this.secondsToTimeString(currentTime)
      } else {
        this.$message.warning('请先加载视频')
      }
    },

    // 格式化时长
    formatDuration(seconds) {
      const hh = Math.floor(seconds / 3600)
      const mm = Math.floor((seconds % 3600) / 60)
      const ss = Math.floor(seconds % 60)
      if (hh > 0) {
        return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
      }
      return `${mm}:${String(ss).padStart(2, '0')}`
    },

    handleClose() {
      // 依靠 v-if="dialogVisible" 自动销毁视频 DOM，无需手动操作 DOM 属性破坏双向绑定
      this.videoQuestions = []
      this.videoDuration = 0
    },

    // 渲染LaTeX公式
    renderLatex(text) {
      if (!text) return ''
      try {
        // 匹配 $...$ 格式的LaTeX公式
        return text.replace(/\$([^$]+)\$/g, (match, formula) => {
          try {
            return katex.renderToString(formula, {
              throwOnError: false,
              displayMode: false
            })
          } catch (e) {
            console.warn('LaTeX渲染失败:', formula, e)
            return match // 如果渲染失败，返回原文
          }
        })
      } catch (e) {
        console.error('LaTeX渲染错误:', e)
        return text
      }
    }
  }
}
</script>

<style scoped>
.question-manager {
  display: flex;
  gap: 20px;
  height: 620px;
  align-items: stretch;
  padding: 10px 0;
}

/* 左边播放器占 50% */
.manager-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fdfdfd;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 15px;
}

/* 右边操作区占 50% */
.manager-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;
  padding-right: 5px;
}

/* 视频预览容器 */
.preview-video-container {
  position: relative;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  min-height: 360px;
}

.preview-video-container video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 隐藏原生播放器 timeline */
video::-webkit-media-controls-timeline {
  display: none !important;
}
video::-moz-media-controls-timeline {
  display: none !important;
}

/* 视频标签轨道 */
.video-markers {
  position: absolute;
  bottom: 12px;
  left: 15px;
  right: 15px;
  height: 20px;
  pointer-events: auto;
  z-index: 10;
  display: flex;
  align-items: center;
}

.markers-timeline-track {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.markers-timeline-played {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #409EFF;
  border-radius: 2px;
}

.markers-timeline-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background: #fff;
  border: 2px solid #409EFF;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: transform 0.1s;
}

.markers-timeline-track:hover .markers-timeline-handle {
  transform: translate(-50%, -50%) scale(1.3);
}

.video-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
  z-index: 2;
}

.video-marker:hover {
  transform: translate(-50%, -50%) translateY(-2px);
  z-index: 20;
}

.video-marker:hover .marker-label {
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-weight: 600;
  padding: 4px 8px;
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, -8px);
}

.video-marker:hover .marker-line {
  background: #409EFF;
  height: 8px;
}

.video-marker:hover .marker-dot {
  transform: scale(1.3);
  background: #409EFF;
}

.marker-label {
  font-size: 11px;
  color: #fff;
  background: rgba(0, 0, 0, 0.85);
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  transition: all 0.2s;
  font-weight: 500;
  line-height: 1.2;
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translate(-50%, 5px);
  opacity: 0;
  visibility: hidden;
}

.marker-line {
  width: 1.5px;
  height: 0;
  background: #409EFF;
  transition: all 0.2s;
  border-radius: 1px;
}

.marker-dot {
  width: 10px;
  height: 10px;
  background: #fff;
  border: 2.5px solid #409EFF;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: all 0.2s;
}

/* 操作区域列表 */
.questions-list {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 15px;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-height: 480px;
}

.table-scroll-wrapper {
  flex-grow: 1;
  overflow-y: auto;
}

.question-preview {
  padding: 4px 0;
}

.question-text {
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
  margin-bottom: 2px;
}

.question-meta {
  margin-top: 4px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  background: #f5f7fa;
  margin: -20px;
  margin-top: 20px;
  border-radius: 0 0 4px 4px;
}

/* 表格样式优化 */
::v-deep .el-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

::v-deep .el-table td {
  padding: 6px 0;
}

::v-deep .el-table--border td,
::v-deep .el-table--border th {
  border-color: #ebeef5;
}

/* 隐藏外层滚动条 */
.manager-right::-webkit-scrollbar {
  width: 6px;
}
.manager-right::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
</style>
